import { action, computed, makeObservable, observable } from 'mobx'
import type { ViewState } from 'react-map-gl/mapbox'

import { IPosition, ISiteLocation } from '~/client/graph'
import BaseMapViewSetUpStore from '~/client/src/shared/components/SitemapHelpers/BaseMapViewSetUp.store'
import { globeDisplayedLocationIds } from '~/client/src/shared/components/SitemapHelpers/models/buildGlobeViewItems'
import MapViewItemBase from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemBase'
import { IMapConfiguration } from '~/client/src/shared/components/SitemapsGallery/MapViewsGallery'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Workflow from '~/client/src/shared/models/Workflow'
import BasemapsStore from '~/client/src/shared/stores/domain/Basemaps.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import { lbsBands } from '~/client/src/shared/types/IHierarchyParent'
import { globeLbsTag } from '~/client/src/shared/utils/pinLocationSnap'
import { IToastOptions, toast } from '~/client/src/shared/utils/toast'
import { areArraysEqual } from '~/client/src/shared/utils/util'

import GlobeView from '../../models/GlobeView'
import LocationBase from '../../models/LocationObjects/LocationBase'
import Sitemap from '../../models/Sitemap'
import GlobeViewsStore from '../../stores/domain/GlobeViews.store'
import LocationAttributesStore from '../../stores/domain/LocationAttributes.store'
import MarkupsStore from '../../stores/domain/Markups.store'
import SitemapsStore from '../../stores/domain/Sitemaps.store'
import TagsStore from '../../stores/domain/Tags.store'
import TilesetsStore from '../../stores/domain/Tilesets.store'
import EventsStore from '../../stores/EventStore/Events.store'
import GlobeViewControlStore from '../../stores/GlobeViewControl.store'
import InitialState from '../../stores/InitialState'
import MapBoxViewerStore from '../MapBoxViewer/MapBoxViewer.store'
import { resolveSelection } from './liveSelection'
import MapMarkupSessionStore from './MapMarkupSession.store'
import MapObjectStackStore, { IOpenStack } from './stack/MapObjectStack.store'
import { taggedMaps } from './stack/taggedMaps'
import {
  findNearestDisplayedInChain,
  IPinAnchorContext,
  resolveWorkflowPinCoordinate,
  resolveWorkflowSourceGlobe,
  workflowPinnedGlobeIds,
} from './workflowPinAnchors'

// One key, so repeat refusals replace the cue rather than stack it.
const WORKFLOW_MAP_FOCUS_TOAST_KEY = 'workflow-map-focus'
export interface IWorkflowFocusOptions {
  /** Whether this map currently draws a pin for the workflow. Read live: a source-map switch
   *  re-runs it, and a snapshot would answer for the map the user just left. */
  isDrawnOnMap?: (workflow: Workflow) => boolean
  /** The map views this surface can show, so the cue can offer one that draws the pin. */
  candidateGlobeIds?: string[]
  /** Fixed chrome the caller lays over the map's right edge. */
  chromeInsetRightPx?: number
  /** False when the caller shows its own detail surface, so the map card would only duplicate it
   *  and cover the pin. The pin still renders selected, and always placed, off the open form. */
  shouldOpenMapCard?: boolean
}

// The default toast life is too short to read a sentence and click a button.
const ACTIONABLE_CUE_TIMEOUT_MS = 10000
// Caps the wait if the thread never goes idle.
const CAMERA_FRAME_TIMEOUT_MS = 600

export interface ILiveLogisticsEntities {
  allForms: Workflow[]
}

// The map a stack row's map link left: data, so the live map view restores it, whatever mounted.
export interface IMapReturn {
  fromMap: IMapConfiguration
  toMapId: string
  name: string
  pose: Partial<ViewState>
  stack: IOpenStack | null
}

export interface ILinkedMap {
  id: string
  name: string
  map: IMapConfiguration
}

// The host's own map switch: guarded against unsaved markups, onSwitched only once it lands.
export interface IMapSwitch {
  selectGlobe: (globe: GlobeView, onSwitched?: () => void) => void
  selectSitemap: (sitemap: Sitemap, onSwitched?: () => void) => void
}

export default class LogisticsMapViewSetUpStore extends BaseMapViewSetUpStore {
  @observable public topOffset: number = 0
  @observable public leftOffset: number = 0
  @observable private selectedWorkflowIds: string[] = []
  @observable public selectedAttribute: MapViewItemBase = null
  public readonly mapBoxViewerStore: MapBoxViewerStore
  public readonly globeViewControlStore: GlobeViewControlStore
  public readonly markupSession?: MapMarkupSessionStore
  private disposeCameraFrame: () => void
  @observable public shouldShowCreateWorkflowBubble: boolean = false
  /**
   * Bumped when the seam retargets the map view by itself. Only this path writes it — the user's
   * own pick goes through `selectGlobe` — so the picker can cue a switch nobody asked for without
   * cueing one they did.
   */
  @observable public systemMapSwitchNonce: number = 0
  public readonly stack = new MapObjectStackStore()
  @observable.ref private mapReturn: IMapReturn = null
  public constructor(
    public readonly sitemapsStore: SitemapsStore,
    public readonly tilesetsStore: TilesetsStore,
    public readonly globeViewsStore: GlobeViewsStore,
    protected readonly basemapsStore: BasemapsStore,
    protected readonly sitemapItemsStore: SitemapItemsStore,
    protected readonly locationAttributesStore: LocationAttributesStore,
    private readonly tagsStore: TagsStore,
    private readonly state: InitialState,
    private readonly liveEntities: ILiveLogisticsEntities,
    eventsStore?: EventsStore,
    private readonly permitId?: () => string,
    markupsStore?: MarkupsStore,
  ) {
    super(
      sitemapsStore,
      globeViewsStore,
      basemapsStore,
      sitemapItemsStore,
      locationAttributesStore,
    )

    this.globeViewControlStore = new GlobeViewControlStore(
      eventsStore,
      globeViewsStore,
      sitemapItemsStore,
      locationAttributesStore,
    )

    this.mapBoxViewerStore = new MapBoxViewerStore(
      sitemapsStore,
      tilesetsStore,
      sitemapItemsStore,
      locationAttributesStore,
      state,
      this.globeViewControlStore,
      basemapsStore,
      eventsStore,
      () => this.selectedAttribute,
      this.selectItem,
    )
    // Map-first drawing session (STX1-686); only hosts that pass a
    // markupsStore (the Workflows/Logistics maps) get one.
    if (markupsStore) {
      this.markupSession = new MapMarkupSessionStore(
        markupsStore,
        this.mapBoxViewerStore,
        () => this.state.activeProject.id,
      )
    }
    makeObservable(this)
  }

  public dispose(): void {
    this.disposeCameraFrame?.()
    this.markupSession?.dispose()
    this.globeViewControlStore.dispose()
  }

  public get hierarchyAttributes(): LocationBase[] {
    return this.locationAttributesStore.allAttributes
  }

  public get displayedPermitId(): string {
    return this.permitId?.()
  }

  public get isGlobeMode() {
    return !!this.globeViewControlStore.selectedGlobeView
  }

  // The picker's render gate reads the view's props and workflow types, so the view
  // supplies it; read live, a gate closing under an open picker releases the chrome.
  @observable.ref private createBubbleGate: () => boolean = () => false

  @action.bound
  public setCreateBubbleGate(gate: () => boolean): void {
    this.createBubbleGate = gate
  }

  // The type picker is the first step of the same creation, so the chrome the Composer
  // hides stays hidden from the pick onward instead of resizing the map at hand-off.
  @computed
  public get isMapFirstCreationActive(): boolean {
    return (
      (this.shouldShowCreateWorkflowBubble && this.createBubbleGate()) ||
      !!this.markupSession?.isCreating
    )
  }

  // Shared by pins and labels so "anchored" cannot diverge between them: the pins use it to
  // lift clear of the marker, the labels to know there should be no label at all.
  @computed
  public get anchorCircleShownIds(): Set<string> {
    return new Set(
      (this.mapBoxViewerStore.displayedGlobeViewItems ?? [])
        .filter(i => i.globeViewItemProperties?.iconProperties?.isDisplayed)
        .map(i => i.id),
    )
  }

  // globeViewId names a map other than the one on screen, for deciding whether a workflow would
  // belong to it before switching there.
  private buildAnchorContext(
    displayedItemIds: Set<string>,
    globeViewId?: string,
  ): IPinAnchorContext {
    const { hierarchyAllAttributesMap } = this.locationAttributesStore
    // A named globe answers for itself or not at all: falling back to the selection would answer
    // about the map on screen while claiming to be about another one.
    const assigned = globeViewId
      ? this.globeViewsStore.globeAssignedLocations(
          this.globeViewsStore.byId.get(globeViewId),
        )
      : this.globeViewControlStore.selectedGlobeViewAssignedLocations

    return {
      getLocation: id => hierarchyAllAttributesMap[id] ?? null,
      displayedItemIds,
      ...globeLbsTag(assigned),
      resolveCoordinates: id =>
        this.globeViewsStore.resolveLocationCoordinates(id) ?? null,
      // anchorCircleShownIds describes the map on screen, so a named globe must not consult it.
      isAnchorCircleShown: globeViewId
        ? () => false
        : id => this.anchorCircleShownIds.has(id),
    }
  }

  // Assembled once so the anchor policy stays a leaf: it needs the hierarchy, what the map draws,
  // what the globe is tagged to, and where a location resolves to.
  @computed
  public get pinAnchorContext(): IPinAnchorContext {
    const items = this.isGlobeMode
      ? this.mapBoxViewerStore.displayedGlobeViewItems
      : this.sortedSitemapAttributes
    return this.buildAnchorContext(new Set((items ?? []).map(i => i.id)))
  }

  @action.bound
  public selectWorkflows(
    permits?: Workflow[],
    item?: MapViewItemBase,
    top?: number,
    left?: number,
  ) {
    // Re-picking what is already picked is a no-op. Rewriting the same selection
    // rebuilds the pin GeoJSON and makes mapbox re-place the symbol, which can blink
    // the pin on every repeat click. Deselection is the popup's job, not a second tap's.
    const workflowIds = permits?.map(permit => permit.id) ?? []

    if (
      workflowIds.length &&
      areArraysEqual(this.selectedWorkflowIds, workflowIds) &&
      this.selectedAttribute === item
    ) {
      return
    }

    this.deselectAll()
    this.selectedAttribute = item
    this.selectedWorkflowIds = workflowIds
    this.topOffset = top
    this.leftOffset = left
  }

  // The one way a workflow becomes the map's active selection. Chain-resolved so an anchored pin
  // (its location off this globe, or not drawn on it) still names a location in the popup header.
  @action.bound
  public activateWorkflows(
    workflows: Workflow[],
    ctx: IPinAnchorContext = this.pinAnchorContext,
  ): void {
    const first = workflows?.[0]
    if (!first) {
      return
    }

    const anchorItemId = findNearestDisplayedInChain(
      ctx,
      first.locations?.[0]?.id,
    )

    this.selectWorkflows(
      workflows,
      (this.mapBoxViewerStore.allGlobeViewItems ?? []).find(
        i => i.id === anchorItemId,
      ),
    )
  }

  // The render context is empty at or below the project-marker zoom, which would report an
  // anchored workflow as unpinned. A fly lands above that band, so resolve against what arrives.
  @computed
  private get focusAnchorContext(): IPinAnchorContext {
    return this.buildAnchorContext(
      new Set(
        (this.mapBoxViewerStore.allGlobeViewItems ?? [])
          .filter(i => i.isVisibleOnMap)
          .map(i => i.id),
      ),
    )
  }

  /**
   * Orient the map to the map view this workflow belongs to: switch there if needed, then make it
   * the active selection and frame its pin. The single seam for "show me this workflow".
   */
  @action.bound
  public focusWorkflowOnMap(
    workflow: Workflow,
    options: IWorkflowFocusOptions = {},
  ): void {
    this.runFocus(workflow, options, false)
  }

  // hasSwitchedMapView: this pass follows a switch, so it must not switch again and the viewport
  // mirror still holds the map being left.
  private runFocus(
    workflow: Workflow,
    options: IWorkflowFocusOptions,
    hasSwitchedMapView: boolean,
  ): void {
    if (!workflow) {
      return
    }

    // Mid-reposition the map centre IS the creation pin (finishMovingPin reads the viewport), and
    // a live creation owns the camera, so a fly would move or strand the user's work.
    if (this.isMarkupSessionHoldingTheCamera) {
      return
    }

    // A frame armed for the map being left must not fire against the one arriving.
    this.disposeCameraFrame?.()

    const {
      isDrawnOnMap,
      candidateGlobeIds,
      chromeInsetRightPx = 0,
      shouldOpenMapCard = true,
    } = options
    const { translator } = Localization
    const isDrawn = !isDrawnOnMap || isDrawnOnMap(workflow)

    // A caller opening its own detail surface owns it alone, so whatever card is up belongs to
    // something else. Stated once here rather than per branch: every outcome below — framed,
    // switched, refused, or a whiteboard selection — has to honour it, and the whiteboard's early
    // return did not.
    if (!shouldOpenMapCard) {
      this.clearMapSelection()
    }

    // A configured id outlives a deleted map view, and selectGlobeById would deselect and strand
    // the stage on one. Filtered once so the cue and the target read the same list.
    const configuredGlobeIds = candidateGlobeIds?.filter(id =>
      this.globeViewsStore.byId.has(id),
    )

    // Filters answer the same on every map view, so no switch can make this true. Ahead of the
    // whiteboard branch too, which would otherwise offer a jump that lands and refuses.
    if (!isDrawn) {
      this.refuseWorkflowFocus(translator.thisWorkflowIsHiddenOnTheMap)
      return
    }

    // A whiteboard has no camera, but its pins carry the same selection.
    if (!this.isGlobeMode) {
      const item = this.getRelatedItems(workflow.locationsWithEquipment)[0]
      if (item) {
        if (shouldOpenMapCard) {
          this.selectWorkflows([workflow], item)
        }
        return
      }
      const sourceGlobeId = this.resolveWorkflowSourceGlobeId(
        workflow,
        configuredGlobeIds,
      )
      this.refuseWorkflowFocus(
        sourceGlobeId
          ? translator.thisWorkflowIsPinnedOnADifferentMapView
          : translator.thisWorkflowHasNoPinOnAnyMapView,
        sourceGlobeId && {
          text: translator.showOnSourceMap,
          onClick: () =>
            this.switchToWorkflowMap(workflow, options, sourceGlobeId),
        },
      )
      return
    }

    const { selectedGlobeViewId } = this.globeViewControlStore
    const targetGlobeId = this.resolveWorkflowSourceGlobeId(
      workflow,
      configuredGlobeIds,
    )

    if (
      !hasSwitchedMapView &&
      targetGlobeId &&
      targetGlobeId !== selectedGlobeViewId
    ) {
      // Not inside switchToWorkflowMap: the whiteboard cue's action is the user asking for it.
      this.systemMapSwitchNonce++
      this.switchToWorkflowMap(workflow, options, targetGlobeId)
      return
    }

    // One read: untracked @computed reads are not memoized, and this scans every attribute.
    const ctx = this.focusAnchorContext
    const coordinates = resolveWorkflowPinCoordinate(
      ctx,
      workflow,
      selectedGlobeViewId,
    )

    if (!coordinates) {
      const pinnedGlobeIds = workflowPinnedGlobeIds(workflow)
      this.refuseWorkflowFocus(
        pinnedGlobeIds.length &&
          !pinnedGlobeIds.some(id => configuredGlobeIds?.includes(id))
          ? translator.thisWorkflowIsPinnedOnADifferentMapView
          : targetGlobeId
            ? translator.thisWorkflowIsNotShownOnThisMapView
            : translator.thisWorkflowHasNoPinOnAnyMapView,
      )
      return
    }

    // Select first: only a selected pin is sure to be placed.
    if (shouldOpenMapCard) {
      this.activateWorkflows([workflow], ctx)
    }
    // Re-picking the active row re-centres on purpose; the selection write no-ops.
    const offset: [number, number] = [-chromeInsetRightPx / 2, 0]
    this.scheduleCameraFrame(() =>
      hasSwitchedMapView
        ? this.mapBoxViewerStore.setViewportFromCoordinates(
            coordinates,
            offset,
            this.mapBoxViewerStore.viewInfo,
          )
        : this.mapBoxViewerStore.setViewportFromCoordinates(
            coordinates,
            offset,
          ),
    )
  }

  /**
   * Run a camera move once the main thread is free. Opening the workflow panel mounts a second
   * mapbox map and blocks ~300ms; issued together, the flight loses its first frames and reads as
   * a jump. See STX1-1127.
   */
  private scheduleCameraFrame(frame: () => void): void {
    this.disposeCameraFrame?.()

    // The map the coordinate was resolved for. Picking another map view inside the deferral window
    // cancels nothing — the picker writes the selection and knows nothing about this frame — so
    // firing it then would fly the arriving map to the previous map's pin, overriding a navigation
    // the user just made. Guarded here rather than at each caller, and rather than asking every
    // selection path to remember.
    const framedMapId = this.mapSelectionKey
    const run = (): void => {
      if (this.mapSelectionKey === framedMapId) {
        frame()
      }
    }

    if (typeof requestIdleCallback === 'function') {
      const handle = requestIdleCallback(run, {
        timeout: CAMERA_FRAME_TIMEOUT_MS,
      })
      this.disposeCameraFrame = () => cancelIdleCallback(handle)
      return
    }

    // Safari has no idle callback; two frames still clear the synchronous commit.
    let handle = requestAnimationFrame(() => {
      handle = requestAnimationFrame(run)
    })
    this.disposeCameraFrame = () => cancelAnimationFrame(handle)
  }

  /** The map selection across both modes, so a globe↔whiteboard switch reads as a change even
   *  where the globe id outlives it — unlike onScreenMapId, which follows what renders. */
  private get mapSelectionKey(): string {
    const { selectedGlobeViewId } = this.globeViewControlStore
    return `${selectedGlobeViewId ?? ''}|${this.selectedSitemapId ?? ''}`
  }

  private get isMarkupSessionHoldingTheCamera(): boolean {
    const session = this.markupSession
    return !!session && (session.isMovingPin || session.isInteracting)
  }

  /** Switch to the map view this workflow belongs to, then decide on the map that arrives. */
  @action.bound
  private switchToWorkflowMap(
    workflow: Workflow,
    options: IWorkflowFocusOptions,
    globeViewId: string,
  ): void {
    // Also the whiteboard cue's action, which fires long after the click.
    if (this.isMarkupSessionHoldingTheCamera) {
      return
    }

    this.deselectAll()
    this.deselectSitemap()
    this.mapReturn = null
    this.globeViewControlStore.selectGlobeById(globeViewId)
    // selectedGlobeViewItems is a plain computed over already-loaded stores, so the arriving map's
    // pin resolves in this same tick.
    this.runFocus(workflow, options, true)
  }

  // No dismiss first: that drops the key, and the next cue would `add` a second toast instead of
  // taking the keyed `update` path.
  private refuseWorkflowFocus(
    message: string,
    cueAction?: IToastOptions['action'],
  ): void {
    this.clearMapSelection()
    toast.info(message, {
      key: WORKFLOW_MAP_FOCUS_TOAST_KEY,
      timeout: cueAction ? ACTIONABLE_CUE_TIMEOUT_MS : undefined,
      action: cueAction,
    })
  }

  /** The configured map view this workflow belongs to, the one on screen included. */
  private resolveWorkflowSourceGlobeId(
    workflow: Workflow,
    candidateGlobeIds: string[],
  ): string | null {
    // One per globe: each build re-sorts every location store, and an untracked @computed read
    // inside an action is never memoized.
    const contextByGlobe = new Map<string, IPinAnchorContext>()
    const candidateContext = (globeViewId: string): IPinAnchorContext => {
      if (!contextByGlobe.has(globeViewId)) {
        contextByGlobe.set(
          globeViewId,
          this.buildAnchorContext(
            this.displayedLocationIdsOn(globeViewId),
            globeViewId,
          ),
        )
      }
      return contextByGlobe.get(globeViewId)
    }

    return resolveWorkflowSourceGlobe(
      workflow,
      candidateGlobeIds,
      (locationId, candidates) =>
        this.globeViewsStore.resolveLocationDisplayContext(
          locationId,
          candidates,
        ),
      globeViewId =>
        !!resolveWorkflowPinCoordinate(
          candidateContext(globeViewId),
          workflow,
          globeViewId,
        ),
    )
  }

  // An off-screen map view has no rendered item list to read.
  private displayedLocationIdsOn(globeViewId: string): Set<string> {
    return globeDisplayedLocationIds({
      globe: this.globeViewsStore.byId.get(globeViewId),
      getSitemapItem: id => this.sitemapItemsStore.byId.get(id),
      getDataObject: assignedId =>
        this.locationAttributesStore.hierarchyAllAttributesMap[assignedId],
    })
  }

  // Decided as the map's render is: a globe id can outlive the switch to a whiteboard.
  @computed
  public get onScreenMapId(): string {
    return this.isGlobeMode
      ? this.globeViewControlStore.selectedGlobeViewId
      : this.selectedSitemapId
  }

  // Offered on the map the link opened only: elsewhere it would name a map the user never left.
  @computed
  public get activeMapReturn(): IMapReturn {
    return this.mapReturn?.toMapId === this.onScreenMapId
      ? this.mapReturn
      : null
  }

  // The maps a location is tagged to, in configured order, the one on screen aside. By tag, not by
  // what a map draws: a level's own plan shows its rooms, not the level.
  public linkedMapsOf(location: LocationBase): ILinkedMap[] {
    return taggedMaps(
      location,
      this.state.workflowsMapIdsList,
      this.onScreenMapId,
    ).flatMap(map => {
      const target = map.globeViewId
        ? this.globeViewsStore.byId.get(map.globeViewId)
        : this.sitemapsStore.byId.get(map.sitemapId)
      return target ? [{ id: target.id, name: target.name, map }] : []
    })
  }

  // A map view the user picks, as one change: a guarded switch that resumes later still lands
  // whole. It ends any way back a map link offered; `onSwitched` runs after, for the switches
  // that set one up. The host wraps it in its unsaved-markup guard.
  @action.bound
  public switchToGlobe(globe: GlobeView, onSwitched?: () => void): void {
    this.deselectSitemap()
    this.globeViewControlStore.selectGlobe(globe)
    this.landOnPickedMap(onSwitched)
  }

  @action.bound
  public switchToSitemap(sitemap: Sitemap, onSwitched?: () => void): void {
    this.globeViewControlStore.deselectGlobe()
    this.selectSitemap(sitemap)
    this.landOnPickedMap(onSwitched)
  }

  private landOnPickedMap(onSwitched?: () => void): void {
    this.mapBoxViewerStore.setViewportFromAddress()
    this.deselectAll()
    this.mapReturn = null
    onSwitched?.()
  }

  // The pill's way back, through the host's guarded switch.
  public mapReturnLink(
    mapSwitch: IMapSwitch,
  ): { name: string; back: () => void } | null {
    const mapReturn = this.activeMapReturn
    return (
      mapReturn && {
        name: mapReturn.name,
        back: () => this.returnFromLinkedMap(mapSwitch),
      }
    )
  }

  // Switches as the map-view pill does, and selects the location in the same step once it lands,
  // where that map draws it.
  @action.bound
  public openLinkedMap(
    { id, map }: ILinkedMap,
    locationId: string,
    mapSwitch: IMapSwitch,
  ): void {
    const from = this.globeViewsStore.byId.get(this.onScreenMapId)
    const { latitude, longitude, zoom, bearing, pitch } =
      this.mapBoxViewerStore.viewport
    const mapReturn: IMapReturn = from && {
      fromMap: { globeViewId: from.id },
      toMapId: id,
      name: from.name,
      pose: { latitude, longitude, zoom, bearing, pitch },
      stack: this.stack.open,
    }
    this.switchMap(map, mapSwitch, () => {
      this.mapReturn = mapReturn
      this.selectArrivedLocation(locationId)
    })
  }

  @action.bound
  public returnFromLinkedMap(mapSwitch: IMapSwitch): void {
    const mapReturn = this.activeMapReturn
    if (!mapReturn) return
    this.switchMap(mapReturn.fromMap, mapSwitch, () => {
      this.mapReturn = null
      this.mapBoxViewerStore.jumpToViewport(mapReturn.pose)
      if (mapReturn.stack) this.stack.reopen(mapReturn.stack)
    })
  }

  private switchMap(
    { globeViewId, sitemapId }: IMapConfiguration,
    { selectGlobe, selectSitemap }: IMapSwitch,
    onSwitched: () => void,
  ): void {
    const globe = globeViewId && this.globeViewsStore.byId.get(globeViewId)
    if (globe) {
      selectGlobe(globe, onSwitched)
      return
    }
    const sitemap = this.sitemapsStore.byId.get(sitemapId)
    if (sitemap) selectSitemap(sitemap, onSwitched)
  }

  // Not the rendered list: at a project-marker zoom it is empty, and the frame below zooms in.
  private selectArrivedLocation(locationId: string): void {
    const items = this.isGlobeMode
      ? this.globeViewControlStore.selectedGlobeViewItems.filter(
          i => i.isVisibleOnMap,
        )
      : this.displayedSitemapItems
    const item = items.find(i => i.id === locationId && !i.isDataLess)
    if (!item) return
    this.selectItem(item)
    // The arriving map's own pose: `viewport` still mirrors the map being left.
    if (this.isGlobeMode) {
      this.mapBoxViewerStore.setViewportFromCoordinates(
        item.sitemapItem.coordinates,
        undefined,
        this.mapBoxViewerStore.viewInfo,
      )
    }
  }

  @action
  public selectItem = (item: MapViewItemBase, top?: number, left?: number) => {
    if (this.hasWorkflowSelection) {
      this.deselectAll()
    }

    this.topOffset = top
    this.leftOffset = left

    this.selectedAttribute = item === this.selectedAttribute ? null : item
  }

  @action.bound
  public clearSelectedAttribute(): void {
    this.selectedAttribute = null
  }

  // Every card the map can own: the attribute one renders when no workflow selection is live,
  // so clearing the list alone just hands it the surface.
  @action.bound
  private clearMapSelection(): void {
    this.selectedWorkflowIds = []
    this.selectedAttribute = null
    this.topOffset = 0
    this.leftOffset = 0
  }

  // The cards plus the pending creation intent. Swapping which card is shown wants
  // clearMapSelection instead.
  @action.bound
  public deselectAll() {
    this.clearMapSelection()
    this.shouldShowCreateWorkflowBubble = false
    this.state.preSelectedSiteLocation = null
  }

  // Cards only, never the creation intent: neither caller — the detail panel closing, or a tap on
  // empty map in compact mode — is the board-clearing gesture.
  public deselectWorkflows = () => {
    if (this.hasWorkflowSelection) {
      this.clearMapSelection()
    }
  }

  public getPermitLocationPositions = (permit: Workflow): IPosition[] => {
    return this.getLocationPositions(permit?.locationsWithEquipment)
  }

  public getRelatedItems = (locations: ISiteLocation[]): MapViewItemBase[] => {
    if (!locations?.length) {
      return []
    }

    return locations.reduce((list, loc) => {
      const relatedItem =
        this.getDisplayedItemById(loc.id) || this.findParentItemByAttrId(loc.id)

      if (relatedItem?.sitemapItemProperties?.iconProperties?.position) {
        list.push(relatedItem)
      }

      return list
    }, [] as MapViewItemBase[])
  }

  public getLocationPositions = (locations: ISiteLocation[]): IPosition[] => {
    if (!locations?.length) {
      return []
    }

    return locations
      .map(({ id }) => {
        const relatedItem =
          this.getDisplayedItemById(id) || this.findParentItemByAttrId(id)

        return relatedItem?.sitemapItemProperties?.iconProperties?.position
      })
      .filter(pos => pos)
  }

  public findParentItemByAttrId = (id: string): MapViewItemBase => {
    const attribute = this.hierarchyAttributes.find(a => a.id === id)

    const parents = attribute?.getHierarchyChainsObjects(
      this.tagsStore.tagStoreByTagTypeMap,
    )

    let closestParent: MapViewItemBase = null

    parents?.some(
      a => (closestParent = this.getDisplayedItemById(a.id)) && !!closestParent,
    )

    return closestParent
  }

  private getDisplayedItemById = (id: string): MapViewItemBase => {
    return this.displayedSitemapItems?.find(item => item.id === id)
  }

  public get shouldShowSelectedAttribute(): boolean {
    return !this.hasWorkflowSelection && !!this.selectedAttribute?.dataObject
  }

  // Reading a live list is what subscribes this to it, so check the ids first.
  @computed
  public get selectedWorkflows(): Workflow[] {
    return this.selectedWorkflowIds.length
      ? resolveSelection(this.selectedWorkflowIds, this.liveEntities.allForms)
      : []
  }

  // Intent, not what survived: false here with selectedAttribute still set would open
  // the Location popup in the deleted workflow's place.
  public get hasWorkflowSelection(): boolean {
    return !!this.selectedWorkflowIds.length
  }

  @computed
  public get sortedSitemapAttributes(): MapViewItemBase[] {
    return (this.displayedSitemapItems || [])
      .filter(item => item.dataObject)
      .sort((a, b) => {
        const aNodeType = a.dataObject.type
        const bNodeType = b.dataObject.type

        const aIndex = lbsBands.indexOf(aNodeType)
        const bIndex = lbsBands.indexOf(bNodeType)

        return bIndex - aIndex
      })
  }

  @action.bound
  public setShouldShowCreateWorkflowBubble(value: boolean) {
    this.shouldShowCreateWorkflowBubble = value
  }
}
